import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import { boardsAPI } from '../services/api';
import SVGViewer from '../components/SVGViewer';
import { appUrl, assetPath } from '../utils/appPaths';
import { formatPinCurrent, formatPinVoltage } from '../utils/compatibilityDisplay';
import { withPinCompatibilityDefaults } from '../utils/compatibilityOptions';
import {
  buildPinoutPreviewSvg,
  normalizeRotation,
  rotateCallouts
} from '../utils/pinCalloutLayout';
import {
  normalizeSvgViewBox,
  parseSvgViewBoxFromMarkup,
  rotateSvgViewBox
} from '../utils/svgViewBox';

const BRAND_LOGO_WIDTH = 480;
const BRAND_LOGO_HEIGHT = 200;

const EmbedContainer = styled.div`
  width: 100%;
  height: 100vh;
  background: #ffffff;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  position: relative;
`;

const BackLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #eff6ff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #3b82f6;
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #dbeafe;
    border-color: #3b82f6;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
  }
`;

const BackLinkLogo = styled.img.attrs({
  width: BRAND_LOGO_WIDTH,
  height: BRAND_LOGO_HEIGHT,
  decoding: 'async',
})`
  height: 16px;
  width: auto;
`;

const PinoutContainer = styled.div`
  height: 100vh;
  position: relative;
`;

const PinInfoOverlay = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  max-width: 320px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: ${props => props.show ? 'block' : 'none'};
`;

const PinInfoTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
`;

const PinInfoSubtitle = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 12px;
`;

const PinInfoContent = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
`;

const PinInfoSection = styled.div`
  background: #f1f5f9;
  padding: 8px;
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 11px;
  line-height: 1.4;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  /* Preserve rich text formatting */
  h1, h2, h3, h4, h5, h6 {
    margin: 8px 0 4px 0;
    font-weight: 600;
    color: #1e293b;
    font-size: 12px;
  }
  
  p {
    margin: 4px 0;
  }
  
  ul, ol {
    margin: 4px 0;
    padding-left: 16px;
  }
  
  li {
    margin: 2px 0;
  }
  
  blockquote {
    border-left: 2px solid #3b82f6;
    padding-left: 8px;
    margin: 8px 0;
    font-style: italic;
    color: #64748b;
  }
  
  code {
    background: #e2e8f0;
    padding: 1px 3px;
    border-radius: 2px;
    font-family: 'Courier New', monospace;
    font-size: 10px;
  }
  
  pre {
    background: #e2e8f0;
    padding: 6px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 8px 0;
  }
  
  pre code {
    background: none;
    padding: 0;
  }
  
  a {
    color: #3b82f6;
    text-decoration: underline;
  }
  
  a:hover {
    color: #2563eb;
  }
  
  strong {
    font-weight: 600;
    color: #1e293b;
  }
  
  em {
    font-style: italic;
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 8px 0;
  }
`;

const PinInfoSpecs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 8px;
`;

const PinInfoSpec = styled.div`
  font-size: 11px;
  
  strong {
    color: #374151;
  }
`;

const PinInfoClose = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #9ca3af;
  padding: 4px;
  
  &:hover {
    color: #6b7280;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #ef4444;
  font-size: 14px;
`;

const safeParseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const parseStoredPinCallouts = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = safeParseJson(value);
    return Array.isArray(parsedValue) ? parsedValue : [];
  }

  return [];
};

const normalizeBoardPayload = (boardPayload, svgPayload) => {
  const boardData = boardPayload && typeof boardPayload === 'object' ? boardPayload : {};
  const svgData = svgPayload && typeof svgPayload === 'object' ? svgPayload : {};

  return {
    ...boardData,
    ...svgData,
    svg_content: svgData.svg_content ?? boardData.svg_content ?? '',
    svg_source_content: svgData.svg_source_content ?? boardData.svg_source_content ?? '',
    svg_display_scale: svgData.svg_display_scale ?? boardData.svg_display_scale ?? 1,
    svg_viewbox: normalizeSvgViewBox(svgData.svg_viewbox ?? svgData.svgViewBox ?? boardData.svg_viewbox ?? boardData.svgViewBox),
    svg_rotation_deg: normalizeRotation(svgData.svg_rotation_deg ?? svgData.svgRotationDeg ?? boardData.svg_rotation_deg ?? boardData.svgRotationDeg ?? 0),
    public_svg_rotation_deg: normalizeRotation(
      svgData.public_svg_rotation_deg
      ?? svgData.publicSvgRotationDeg
      ?? boardData.public_svg_rotation_deg
      ?? boardData.publicSvgRotationDeg
      ?? 0
    ),
    pin_callouts: parseStoredPinCallouts(svgData.pin_callouts ?? svgData.pinCallouts ?? boardData.pin_callouts ?? boardData.pinCallouts),
  };
};

const EmbedBoard = () => {
  const { id } = useParams();
  const [board, setBoard] = useState(null);
  const [pins, setPins] = useState([]);
  const [selectedPin, setSelectedPin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle clicks on SVG pins
  const handleSVGPinClick = (arg) => {
    let payload = (arg && typeof arg === 'object' && 'pin' in arg) ? arg : null;

    if (!payload && arg?.target) {
      const el = arg.target.closest ? arg.target.closest('.pin-rect, .pin-hole, .pin-callout-bubble, .pin-callout-anchor') : null;
      if (el) {
        payload = {
          pin: el.getAttribute('data-pin') || '',
          svgId: el.getAttribute('data-svgid') || el.id || '',
          pinNumber: el.getAttribute('data-pin-number') || ''
        };
      }
    }

    if (!payload) return;

    const { pin: pinName, svgId, pinNumber } = payload;
    const pin = pins.find((currentPin) =>
      (pinName && currentPin.pin_name === pinName) ||
      (svgId && currentPin.svg_id === svgId) ||
      (pinNumber && String(currentPin.pin_number) === String(pinNumber))
    );

    if (pin) {
      setSelectedPin(pin);
    }
  };

  const normalizePinsResponsePayload = (payload) => {
    const rawPins = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

    return rawPins.map((pin) => withPinCompatibilityDefaults(pin));
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [boardResponse, svgResponse, pinsResponse] = await Promise.all([
        boardsAPI.getById(id),
        boardsAPI.getSvg(id),
        boardsAPI.getPins(id)
      ]);
      
      setBoard(normalizeBoardPayload(boardResponse.data, svgResponse.data));
      setPins(normalizePinsResponsePayload(pinsResponse.data));
    } catch (err) {
      console.error('Error fetching board data:', err);
      setError('Failed to load board data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const calloutSourceSvg = board?.svg_source_content || board?.svg_content || '';
  const boardSvgRotation = useMemo(
    () => normalizeRotation(board?.svg_rotation_deg ?? 0),
    [board?.svg_rotation_deg]
  );
  const publicSvgRotation = useMemo(
    () => normalizeRotation(board?.public_svg_rotation_deg ?? 0),
    [board?.public_svg_rotation_deg]
  );
  const finalPublicRotation = useMemo(
    () => normalizeRotation(boardSvgRotation + publicSvgRotation),
    [boardSvgRotation, publicSvgRotation]
  );
  const storedViewBox = useMemo(
    () => (
      normalizeSvgViewBox(board?.svg_viewbox ?? board?.svgViewBox)
      || parseSvgViewBoxFromMarkup(board?.svg_content || calloutSourceSvg)
    ),
    [board?.svgViewBox, board?.svg_content, board?.svg_viewbox, calloutSourceSvg]
  );
  const publicPreviewCallouts = useMemo(() => {
    const normalizedCallouts = Array.isArray(board?.pin_callouts) ? board.pin_callouts : [];

    if (!calloutSourceSvg || finalPublicRotation === boardSvgRotation) {
      return normalizedCallouts;
    }

    return rotateCallouts(
      normalizedCallouts,
      calloutSourceSvg,
      boardSvgRotation,
      finalPublicRotation
    );
  }, [board?.pin_callouts, boardSvgRotation, calloutSourceSvg, finalPublicRotation]);
  const publicPreviewViewBox = useMemo(() => {
    if (!storedViewBox || !calloutSourceSvg || finalPublicRotation === boardSvgRotation) {
      return storedViewBox;
    }

    return rotateSvgViewBox(
      storedViewBox,
      calloutSourceSvg,
      boardSvgRotation,
      finalPublicRotation
    );
  }, [boardSvgRotation, calloutSourceSvg, finalPublicRotation, storedViewBox]);
  const publicPreviewSvg = useMemo(() => {
    if (!calloutSourceSvg) {
      return board?.svg_content || '';
    }

    return buildPinoutPreviewSvg(calloutSourceSvg, pins || [], publicPreviewCallouts, finalPublicRotation, {
      viewBoundsMode: 'board',
      lockedViewBox: publicPreviewViewBox || null,
      normalizeCalloutSize: false,
    });
  }, [
    board?.svg_content,
    calloutSourceSvg,
    finalPublicRotation,
    pins,
    publicPreviewCallouts,
    publicPreviewViewBox,
  ]);

  if (loading) {
    return (
      <EmbedContainer>
        <LoadingSpinner>Loading board...</LoadingSpinner>
      </EmbedContainer>
    );
  }

  if (error || !board) {
    return (
      <EmbedContainer>
        <ErrorMessage>{error || 'Board not found'}</ErrorMessage>
      </EmbedContainer>
    );
  }

  return (
    <EmbedContainer>
      <PinoutContainer>
        <SVGViewer
          svgContent={publicPreviewSvg || board.svg_content}
          pins={pins}
          displayScale={board.svg_display_scale}
          headerLeftContent={(
            <BackLink href={appUrl(`/board/${id}`)} target="_blank" rel="noopener noreferrer">
              <ArrowLeft size={14} />
              View on
              <BackLinkLogo src={assetPath('/logo.png')} alt="MicroPinouts" />
            </BackLink>
          )}
          onPinClick={handleSVGPinClick}
          enableZoom={true}
          enablePan={true}
          enableWheelZoom={false}
          showWheelZoomToggle={true}
          style={{ height: '100%', width: '100%' }}
        />
        
        <PinInfoOverlay show={!!selectedPin}>
          {selectedPin && (
            <>
              <PinInfoClose onClick={() => setSelectedPin(null)}>
                ×
              </PinInfoClose>
              <PinInfoTitle>{selectedPin.pin_name}</PinInfoTitle>
              <PinInfoSubtitle>Pin {selectedPin.pin_number}</PinInfoSubtitle>
              
              <PinInfoContent>
                {selectedPin.functions && (
                  <PinInfoSection>
                    <strong>Primary Function:</strong><br />
                    {selectedPin.functions}
                  </PinInfoSection>
                )}
                
                {selectedPin.alternate_functions && (
                  <PinInfoSection>
                    <strong>Alternate Functions:</strong><br />
                    {selectedPin.alternate_functions}
                  </PinInfoSection>
                )}
                
                {(formatPinVoltage(selectedPin) || formatPinCurrent(selectedPin) || selectedPin.frequency_range) && (
                  <PinInfoSpecs>
                    {formatPinVoltage(selectedPin) && (
                      <PinInfoSpec>
                        <strong>Voltage:</strong><br />
                        {formatPinVoltage(selectedPin)}
                      </PinInfoSpec>
                    )}
                    {formatPinCurrent(selectedPin) && (
                      <PinInfoSpec>
                        <strong>Current:</strong><br />
                        {formatPinCurrent(selectedPin)}
                      </PinInfoSpec>
                    )}
                    {selectedPin.frequency_range && (
                      <PinInfoSpec>
                        <strong>Frequency:</strong><br />
                        {selectedPin.frequency_range}
                      </PinInfoSpec>
                    )}
                  </PinInfoSpecs>
                )}
                
                {selectedPin.description && (
                  <PinInfoSection>
                    <strong>Description:</strong><br />
                    <div dangerouslySetInnerHTML={{ __html: selectedPin.description }} />
                  </PinInfoSection>
                )}
                
                {!selectedPin.functions && !selectedPin.alternate_functions && !selectedPin.description && !formatPinVoltage(selectedPin) && !formatPinCurrent(selectedPin) && !selectedPin.frequency_range && (
                  <PinInfoSection>
                    No additional information available
                  </PinInfoSection>
                )}
              </PinInfoContent>
            </>
          )}
        </PinInfoOverlay>
      </PinoutContainer>
    </EmbedContainer>
  );
};

export default EmbedBoard;
